import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '/api';

export const useServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [notificationTemplateText, setNotificationTemplateText] = useState(`INFORMACIÓN DE ACCESO:
    --------------------
    🎬 Servicio: {{nombre_servicio}}
    📧 Usuario: {{correo}}
    🔑 Contraseña: {{contraseña}}
    👤 Perfil: {{perfil}}
    🔢 PIN: {{pin}}
    ⏳ Expira: {{fecha_vencimiento}}
    ¡Disfruta tu servicio!
    📩 Para soporte, contáctanos.`);
  const [newService, setNewService] = useState({ name: '', url: '', price: 0, maxProfiles: 0, image: '' });
  const [currentServicePage, setCurrentServicePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/services`);
      setServices(response.data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar los servicios. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveService = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const serviceData = {
        name: newService.name,
        url: newService.url,
        price: parseFloat(newService.price),
        maxProfiles: parseInt(newService.maxProfiles),
        image: newService.image || 'https://via.placeholder.com/48'
      };

      if (newService.id) {
        await axios.put(`${API_URL}/services/${newService.id}`, serviceData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/services`, serviceData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      await fetchServices();
      setNewService({ name: '', url: '', price: 0, maxProfiles: 0, image: '' });
      setShowServiceForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar el servicio. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este servicio?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/services/${serviceId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchServices();
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el servicio. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditService = (service) => {
    setNewService({
      id: service.id,
      name: service.name,
      url: service.url,
      price: service.price,
      maxProfiles: service.max_profiles || service.maxProfiles,
      image: service.image
    });
    setShowServiceForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        await ensureFreshCsrfToken();
        
        const formData = new FormData();
        formData.append('image', file);
        
        const response = await axios.post(`${API_URL}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-CSRF-Token': csrfToken
          }
        });
        
        setNewService({ ...newService, image: response.data.imageUrl });
      } catch (error) {
        console.error('Error al subir imagen:', error);
        alert('Error al subir la imagen. Por favor, intente de nuevo.');
      }
    }
  };

  const handleTemplateEdit = (serviceId) => {
    const savedTemplates = JSON.parse(localStorage.getItem('service-templates') || '{}');
    const serviceTemplateText = savedTemplates[serviceId]
      || Object.values(getDefaultTemplate()).join('\n');
    setNotificationTemplateText(serviceTemplateText);
    setSelectedServiceId(serviceId);
    setShowTemplateForm(true);
  };

  const handleSaveTemplate = () => {
    const savedTemplates = JSON.parse(localStorage.getItem('service-templates') || '{}');
    savedTemplates[selectedServiceId] = notificationTemplateText;
    localStorage.setItem('service-templates', JSON.stringify(savedTemplates));
    setShowTemplateForm(false);
    setSelectedServiceId(null);
    alert('Plantilla guardada correctamente');
  };

  const getServiceTemplate = (serviceId) => {
    const saved = JSON.parse(localStorage.getItem('service-templates') || '{}');
    return saved[serviceId]
      || Object.values(getDefaultTemplate()).join('\n');
  };

  const getDefaultTemplate = () => ({
    header: "INFORMACIÓN DE ACCESO:",
    separator: "--------------------",
    service: "🎬 Servicio: {{nombre_servicio}}",
    email: "📧 Correo: {{correo}}",
    password: "🔐 Contraseña: {{contraseña}}",
    profile: "👤 Perfil: {{perfil}}",
    pin: "🔢 PIN: {{pin}}",
    expiry: "📅 Vencimiento: {{fecha_vencimiento}}",
    footer1: "🔸 Este acceso ha sido proporcionado por ℂ𝕝𝕠𝕧𝕖𝕣. Disfruta tu contenido sin interrupciones.",
    footer2: "📩 Para soporte, contáctanos."
  });

  const getPaginatedServices = () => {
    const startIndex = (currentServicePage - 1) * itemsPerPage;
    return services.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleNextPage = () => {
    if (currentServicePage * itemsPerPage < services.length) {
      setCurrentServicePage(currentServicePage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentServicePage > 1) {
      setCurrentServicePage(currentServicePage - 1);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentServicePage(1);
  };

  return {
    services,
    loading,
    error,
    setError,
    showServiceForm,
    setShowServiceForm,
    showTemplateForm,
    setShowTemplateForm,
    selectedServiceId,
    notificationTemplateText,
    setNotificationTemplateText,
    newService,
    setNewService,
    currentServicePage,
    itemsPerPage,
    fetchServices,
    handleSaveService,
    handleDeleteService,
    handleEditService,
    handleImageUpload,
    handleTemplateEdit,
    handleSaveTemplate,
    getServiceTemplate,
    getDefaultTemplate,
    getPaginatedServices,
    handleNextPage,
    handlePrevPage,
    handleItemsPerPageChange
  };
};
